import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import PropTypes from 'prop-types';
import { DayPicker } from 'react-day-picker';
import moment from 'moment';

// Utils
import bemify from 'utils/bemify';

// Components
import Icon from 'components/icon';
import Input from 'components/input';
import Select from 'components/select';

// Styles
import stylesModule from './styles.module.scss';


const styles = bemify(stylesModule);

const MINUTES_IN_DAY = 24 * 60;

const pad = value => String(value).padStart(2, '0');

const startOfDay = (date) => {
  const start = new Date(date);

  start.setHours(0, 0, 0, 0);

  return start;
};

const mergeDateAndTime = (date, hours, minutes) => {
  const merged = new Date(date);

  merged.setHours(hours, minutes, 0, 0);

  return merged;
};

// Builds the selectable time slots for a given day, honouring the min/max bounds
// and the opening-hours availability predicate (isTimeAllowed).
const buildTimeOptions = ({ day, isTimeAllowed, maxDateTime, minDateTime, timeInterval }) => {
  if (!day) return [];

  const options = [];

  for (let minutes = 0; minutes < MINUTES_IN_DAY; minutes += timeInterval) {
    const candidate = mergeDateAndTime(day, Math.floor(minutes / 60), minutes % 60);

    const withinMin = !minDateTime || candidate.getTime() >= minDateTime.getTime();
    const withinMax = !maxDateTime || candidate.getTime() <= maxDateTime.getTime();
    const allowedByHours = typeof isTimeAllowed !== 'function' || isTimeAllowed(candidate);

    if (withinMin && withinMax && allowedByHours) {
      options.push({
        date: candidate,
        label: `${pad(Math.floor(minutes / 60))}:${pad(minutes % 60)}`
      });
    }
  }

  return options;
};

const DateTimePicker = ({
  className = '',
  description = null,
  disabled = false,
  endDate = null,
  filterDate = null,
  hideTime = false,
  isTimeAllowed = null,
  label = null,
  locale = null,
  maxDateTime = null,
  minDateTime = null,
  name,
  onBlur = () => {},
  onChange = () => {},
  placeholder = '',
  required = false,
  selectsRange = false,
  startDate = null,
  timeInterval = 15,
  validationError = null,
  value = null
}) => {
  const [open, setOpen] = useState(false);
  const [popoverPosition, setPopoverPosition] = useState({ bottom: null, left: 0, maxHeight: 0, top: null });
  const containerRef = useRef(null);
  const popoverRef = useRef(null);

  // The popover is portalled to <body> so it is never clipped by a scrolling
  // ancestor (e.g. the checkout form column). It anchors to the field — opening
  // below by default, or flipping to sit directly above the field when there is
  // more room there (mobile) — and caps its height to the viewport.
  const updatePopoverPosition = useCallback(() => {
    if (!containerRef.current) return;

    const rect = containerRef.current.getBoundingClientRect();
    const margin = 8;
    const spaceBelow = window.innerHeight - rect.bottom - margin;
    const spaceAbove = rect.top - margin;

    if (spaceBelow < 360 && spaceAbove > spaceBelow) {
      // Flip up: anchor the popover's bottom to the top of the field.
      setPopoverPosition({
        bottom: (window.innerHeight - rect.top) + margin,
        left: rect.left,
        maxHeight: Math.max(180, spaceAbove),
        top: null
      });
    } else {
      setPopoverPosition({
        bottom: null,
        left: rect.left,
        maxHeight: Math.max(180, spaceBelow),
        top: rect.bottom + margin
      });
    }
  }, []);

  const singleSelectedDate = value ? moment(value).toDate() : null;
  const rangeStartDate = startDate ? moment(startDate).toDate() : null;
  const rangeEndDate = endDate ? moment(endDate).toDate() : null;

  const inputDescription = validationError || description;

  // Close on outside click / Escape while open, mirroring the previous popper behaviour.
  useEffect(() => {
    if (!open) return () => {};

    updatePopoverPosition();

    const handlePointerDown = (event) => {
      const insideField = containerRef.current?.contains(event.target);
      const insidePopover = popoverRef.current?.contains(event.target);

      if (!insideField && !insidePopover) {
        setOpen(false);
        onBlur();
      }
    };

    const handleKeyDown = (event) => {
      if (event.key === 'Escape') {
        setOpen(false);
        onBlur();
      }
    };

    document.addEventListener('mousedown', handlePointerDown);
    document.addEventListener('keydown', handleKeyDown);
    window.addEventListener('resize', updatePopoverPosition);
    window.addEventListener('scroll', updatePopoverPosition, true);

    return () => {
      document.removeEventListener('mousedown', handlePointerDown);
      document.removeEventListener('keydown', handleKeyDown);
      window.removeEventListener('resize', updatePopoverPosition);
      window.removeEventListener('scroll', updatePopoverPosition, true);
    };
  }, [open, onBlur, updatePopoverPosition]);

  // Disabled-day matchers: outside the min/max window, or rejected by filterDate.
  const disabledMatchers = useMemo(() => {
    const matchers = [];

    if (minDateTime) matchers.push({ before: startOfDay(minDateTime) });
    if (maxDateTime) matchers.push({ after: startOfDay(maxDateTime) });
    if (typeof filterDate === 'function') matchers.push(day => !filterDate(day));

    // Date+time mode: disable any day that has no selectable time slot left — e.g.
    // today once the current time is already past the last allowed pickup. Without
    // this the day stays clickable and selection silently falls back to 00:00.
    if (!hideTime && !selectsRange) {
      matchers.push(day => buildTimeOptions({ day, isTimeAllowed, maxDateTime, minDateTime, timeInterval }).length === 0);
    }

    return matchers;
  }, [minDateTime, maxDateTime, filterDate, hideTime, selectsRange, isTimeAllowed, timeInterval]);

  const timeOptions = useMemo(
    () => (hideTime || selectsRange ?
      [] :
      buildTimeOptions({ day: singleSelectedDate, isTimeAllowed, maxDateTime, minDateTime, timeInterval })),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [hideTime, selectsRange, value, isTimeAllowed, maxDateTime, minDateTime, timeInterval]
  );

  const selectedTimeLabel = singleSelectedDate ? moment(singleSelectedDate).format('HH:mm') : null;

  const displayValue = useMemo(() => {
    if (selectsRange) {
      if (!rangeStartDate) return '';

      const formattedStart = moment(rangeStartDate).format('DD/MM/YYYY');

      return rangeEndDate ? `${formattedStart} - ${moment(rangeEndDate).format('DD/MM/YYYY')}` : formattedStart;
    }

    if (!singleSelectedDate) return '';

    return moment(singleSelectedDate).format(hideTime ? 'DD/MM/YYYY' : 'DD/MM/YYYY HH:mm');
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectsRange, value, startDate, endDate, hideTime]);

  const closePicker = () => {
    setOpen(false);
    onBlur();
  };

  const handleTriggerClick = () => {
    if (disabled) return;

    setOpen(previousOpen => !previousOpen);
  };

  const handleRangeSelect = (range) => {
    onChange([range?.from ?? null, range?.to ?? null]);

    if (range?.from && range?.to) closePicker();
  };

  const handleDaySelect = (day) => {
    if (!day) {
      onChange(null);

      return;
    }

    if (hideTime) {
      onChange(startOfDay(day));
      closePicker();

      return;
    }

    // Date + time: keep the previously chosen time when it is still valid for the
    // newly selected day, otherwise fall back to the first available slot.
    const dayOptions = buildTimeOptions({ day, isTimeAllowed, maxDateTime, minDateTime, timeInterval });
    const previousTimeLabel = singleSelectedDate ? moment(singleSelectedDate).format('HH:mm') : null;
    const matchingOption = dayOptions.find(option => option.label === previousTimeLabel);

    if (matchingOption) {
      onChange(matchingOption.date);
    } else if (dayOptions.length) {
      onChange(dayOptions[0].date);
    } else {
      onChange(mergeDateAndTime(day, 0, 0));
    }
  };

  const handleTimeSelect = (optionDate) => {
    onChange(optionDate);
    closePicker();
  };

  const dayPickerProps = { disabled: disabledMatchers };

  if (locale) dayPickerProps.locale = locale;

  if (selectsRange) {
    const selectedRange = {};

    if (rangeStartDate) selectedRange.from = rangeStartDate;
    if (rangeEndDate) selectedRange.to = rangeEndDate;

    dayPickerProps.mode = 'range';
    dayPickerProps.onSelect = handleRangeSelect;
    dayPickerProps.selected = selectedRange;
  } else {
    dayPickerProps.mode = 'single';
    dayPickerProps.onSelect = handleDaySelect;

    if (singleSelectedDate) dayPickerProps.selected = singleSelectedDate;
  }

  return (
    <div
      ref={containerRef}
      className={`${styles.dateTimePicker}${className ? ` ${className}` : ''}`}
    >
      <Input
        block
        description={inputDescription}
        disabled={disabled}
        id={name}
        label={label}
        name={name}
        placeholder={placeholder}
        readOnly
        required={required}
        rightSlot={(
          <Icon
            className={styles.dateTimePicker__icon}
            name="calendar"
          />
        )}
        slotMarginSize="s"
        validationState={validationError ? 'error' : 'default'}
        value={displayValue}
        onChange={() => {}}
        onClick={handleTriggerClick}
      />
      {open && typeof document !== 'undefined' && createPortal(
        <div
          ref={popoverRef}
          className={styles.dateTimePicker__popover}
          style={{
            bottom: popoverPosition.bottom === null ? null : `${popoverPosition.bottom}px`,
            left: `${popoverPosition.left}px`,
            maxHeight: `${popoverPosition.maxHeight}px`,
            top: popoverPosition.top === null ? null : `${popoverPosition.top}px`
          }}
        >
          <div className={styles.dateTimePicker__panel}>
            <DayPicker
              defaultMonth={singleSelectedDate || rangeStartDate || minDateTime || new Date()}
              showOutsideDays
              weekStartsOn={1}
              {...dayPickerProps}
            />
            {!hideTime && !selectsRange && timeOptions.length > 0 && (
              <>
                {/* Desktop: scrolling list of time chips */}
                <div className={styles.dateTimePicker__times}>
                  {timeOptions.map(option => (
                    <button
                      key={option.label}
                      className={styles.dateTimePicker__timeOption}
                      data-selected={option.label === selectedTimeLabel}
                      type="button"
                      onClick={() => handleTimeSelect(option.date)}
                    >
                      {option.label}
                    </button>
                  ))}
                </div>
                {/* Mobile: native select (renders as the OS time picker) */}
                <div className={styles.dateTimePicker__timeSelect}>
                  <Select
                    id={`${name}-time`}
                    native
                    options={timeOptions.map(option => ({ text: option.label, value: option.label }))}
                    selectedOption={selectedTimeLabel || ''}
                    onChange={(selectedLabel) => {
                      const matchingOption = timeOptions.find(option => option.label === selectedLabel);

                      if (matchingOption) handleTimeSelect(matchingOption.date);
                    }}
                  />
                </div>
              </>
            )}
          </div>
        </div>,
        document.body
      )}
    </div>
  );
};

DateTimePicker.propTypes = {
  className: PropTypes.string,
  description: PropTypes.oneOfType([PropTypes.string, PropTypes.node]),
  disabled: PropTypes.bool,
  endDate: PropTypes.oneOfType([PropTypes.instanceOf(Date), PropTypes.oneOf([null])]),
  filterDate: PropTypes.func,
  hideTime: PropTypes.bool,
  isTimeAllowed: PropTypes.func,
  label: PropTypes.string,
  // eslint-disable-next-line react/forbid-prop-types
  locale: PropTypes.object,
  maxDateTime: PropTypes.instanceOf(Date),
  minDateTime: PropTypes.instanceOf(Date),
  name: PropTypes.string.isRequired,
  onBlur: PropTypes.func,
  onChange: PropTypes.func,
  placeholder: PropTypes.string,
  required: PropTypes.bool,
  selectsRange: PropTypes.bool,
  startDate: PropTypes.oneOfType([PropTypes.instanceOf(Date), PropTypes.oneOf([null])]),
  timeInterval: PropTypes.number,
  validationError: PropTypes.string,
  value: PropTypes.oneOfType([
    PropTypes.instanceOf(Date),
    PropTypes.string,
    PropTypes.number,
    PropTypes.oneOf([null])
  ])
};

export default DateTimePicker;
